import type { Listing } from "@/data/listings";
import { FIRST_LESSON_LABEL } from "@/api/listings";

import { useEffect, useMemo, useState } from "react";
import { Link } from "@heroui/link";
import { m } from "framer-motion";
import clsx from "clsx";

import { slugify } from "@/utils/seo";
import { useMotionPreferences } from "@/utils/motion";
import { buildFallbackAvatarUrl, resolveAvatarUrl } from "@/utils/avatar";
import { formatTutorDisplayName } from "@/utils/displayName";

const STUDENT_LEVEL_LABELS: Record<string, string> = {
  college: "Collège",
  lycee: "Lycée",
  superieur: "Supérieur",
  adultes: "Adultes",
};

export type ListingCardVariant = "default" | "compact" | "featured";

type ListingCardProps = {
  listing: Listing;
  ctaLabel?: string;
  /**
   * - `default`: vertical card (16:10 image) for marketplace grids
   * - `compact`: horizontal mini-card for sidebars, related strips
   * - `featured`: vertical hero card (4:3 image) for top-of-page highlights
   */
  variant?: ListingCardVariant;
};

export default function ListingCard({
  listing,
  ctaLabel = "Voir le profil",
  variant = "default",
}: ListingCardProps) {
  const { shouldReduceMotion } = useMotionPreferences();
  const displayName = useMemo(
    () => formatTutorDisplayName(listing.ownerName),
    [listing.ownerName],
  );
  const fallbackAvatarUrl = useMemo(
    () => buildFallbackAvatarUrl(listing.ownerName),
    [listing.ownerName],
  );
  const resolvedAvatarUrl = useMemo(
    () => resolveAvatarUrl(listing.ownerAvatarUrl),
    [listing.ownerAvatarUrl],
  );
  const [avatarUrl, setAvatarUrl] = useState(
    resolvedAvatarUrl || fallbackAvatarUrl,
  );

  useEffect(() => {
    setAvatarUrl(resolvedAvatarUrl || fallbackAvatarUrl);
  }, [fallbackAvatarUrl, resolvedAvatarUrl]);

  const detailHref = `/cours/${slugify(listing.city)}/${slugify(listing.subject)}/${listing.slug}`;
  const isTopRated = listing.rating >= 4.8;
  const isVerified = listing.reviews >= 50;
  const isResponsive = listing.reviews >= 80;
  const hasFirstLesson = listing.profileSections.firstLesson.enabled;
  const visibleLevels = listing.profileSections.studentLevels.slice(0, 2);

  const onImgError = () => {
    if (avatarUrl !== fallbackAvatarUrl) setAvatarUrl(fallbackAvatarUrl);
  };

  /* ── COMPACT variant — horizontal mini-card ──────────────────── */
  if (variant === "compact") {
    return (
      <m.article
        className="group editorial-surface relative flex min-w-0 overflow-hidden rounded-2xl"
        transition={{ type: "spring", stiffness: 260, damping: 22 }}
        whileHover={shouldReduceMotion ? undefined : { y: -2 }}
        whileTap={shouldReduceMotion ? undefined : { scale: 0.99 }}
      >
        <Link
          aria-label={`${listing.title} par ${displayName}`}
          className="absolute inset-0 z-30"
          href={detailHref}
        />

        {/* Image — square, 96-112px */}
        <div className="relative aspect-square w-24 shrink-0 overflow-hidden sm:w-28">
          <img
            alt={displayName}
            className={clsx(
              "h-full w-full object-cover transition-transform duration-500 ease-out",
              !shouldReduceMotion && "group-hover:scale-105",
            )}
            decoding="async"
            loading="lazy"
            src={avatarUrl}
            onError={onImgError}
          />
          {isTopRated && (
            <span className="absolute left-1.5 top-1.5 inline-flex items-center rounded-full bg-primary/95 px-1.5 py-0.5 text-[9.5px] font-semibold text-primary-foreground shadow-md">
              ⭐
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex min-w-0 flex-1 flex-col justify-between gap-1.5 p-3">
          <div className="min-w-0 space-y-0.5">
            <p className="editorial-overline truncate text-[9.5px] text-primary">
              {listing.subject}
            </p>
            <h3 className="line-clamp-1 text-[14px] font-semibold leading-tight text-foreground transition-colors group-hover:text-primary">
              {listing.title}
            </h3>
            <p className="line-clamp-1 text-[11.5px] text-default-500">
              {displayName} · {listing.city}
            </p>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-foreground">
              <StarIcon />
              {listing.rating.toFixed(1)}
              <span className="text-[10.5px] font-normal text-default-500">
                ({listing.reviews})
              </span>
            </span>
            <span className="text-[12.5px] font-bold text-foreground">
              {listing.price}€
              <span className="text-[10px] font-medium text-default-500">
                /h
              </span>
            </span>
          </div>
        </div>
      </m.article>
    );
  }

  /* ── DEFAULT and FEATURED — vertical card ────────────────────── */
  const isFeatured = variant === "featured";

  return (
    <m.article
      className={clsx(
        "group editorial-surface relative flex h-full min-w-0 flex-col overflow-hidden rounded-2xl",
        isFeatured && "ring-1 ring-primary/30",
      )}
      transition={{ type: "spring", stiffness: 260, damping: 22 }}
      whileHover={shouldReduceMotion ? undefined : { y: -4 }}
      whileTap={shouldReduceMotion ? undefined : { scale: 0.995 }}
    >
      {/* Featured: saffron accent line at top */}
      {isFeatured && (
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 z-20 h-[3px] bg-gradient-to-r from-transparent via-primary to-transparent"
        />
      )}

      {/* Saffron sheen sweep on hover */}
      {!shouldReduceMotion && (
        <div
          aria-hidden
          className="pointer-events-none absolute -inset-px z-20 overflow-hidden rounded-2xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        >
          <div className="absolute -inset-[100%] -translate-x-full bg-gradient-to-r from-transparent via-primary/15 to-transparent transition-transform duration-1000 ease-out group-hover:translate-x-full" />
        </div>
      )}

      {/* Image — 16:10 default, 4:3 featured */}
      <div
        className={clsx(
          "relative w-full overflow-hidden",
          isFeatured ? "aspect-[4/3]" : "aspect-[16/10]",
        )}
      >
        <img
          alt={displayName}
          className={clsx(
            "h-full w-full object-cover transition-transform duration-500 ease-out",
            !shouldReduceMotion && "group-hover:scale-105",
          )}
          decoding="async"
          loading="lazy"
          src={avatarUrl}
          onError={onImgError}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-black/15" />

        {/* Trust badges (top-left) */}
        <div className="absolute left-3 top-3 flex flex-wrap gap-1.5">
          {isVerified && <TrustBadge tone="verified">✓ Vérifié</TrustBadge>}
          {isTopRated && <TrustBadge tone="top">⭐ Top noté</TrustBadge>}
          {isResponsive && (
            <TrustBadge tone="fast">⚡ Répond &lt; 2h</TrustBadge>
          )}
        </div>

        {/* Featured ribbon (top-right) */}
        {isFeatured && (
          <div className="absolute right-3 top-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[10.5px] font-semibold uppercase tracking-wide text-[#191B36] shadow-lg backdrop-blur-md">
              ✨ Mis en avant
            </span>
          </div>
        )}

        {/* First-lesson offer (bottom-left) */}
        {hasFirstLesson && (
          <div className="absolute bottom-3 left-3">
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/95 px-2.5 py-1 text-[11px] font-semibold text-primary-foreground shadow-md shadow-primary/30">
              🎁 {listing.profileSections.firstLesson.label ||
                FIRST_LESSON_LABEL}
            </span>
          </div>
        )}
      </div>

      {/* Body */}
      <div
        className={clsx(
          "flex flex-1 flex-col gap-3",
          isFeatured ? "p-5" : "p-4",
        )}
      >
        {/* Subject overline + Title */}
        <div className="min-w-0 space-y-1">
          <p className="editorial-overline truncate text-primary">
            {listing.subject}
          </p>
          <h3
            className={clsx(
              "font-bold leading-snug text-foreground",
              isFeatured ? "text-[20px]" : "text-[17px]",
            )}
          >
            <Link
              className="line-clamp-2 text-foreground decoration-primary decoration-2 underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary/60"
              href={detailHref}
            >
              {listing.title}
            </Link>
          </h3>
        </div>

        {/* Tutor meta */}
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-default-600">
          {listing.ownerId ? (
            <Link
              className="font-medium text-foreground transition-colors hover:text-primary"
              href={`/profil/${listing.ownerId}`}
            >
              {displayName}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{displayName}</span>
          )}
          <span aria-hidden className="text-default-300">
            ·
          </span>
          <Link
            className="text-default-600 transition-colors hover:text-primary"
            href={`/cours/${slugify(listing.city)}`}
          >
            {listing.city}
          </Link>
          {listing.isOnline && (
            <>
              <span aria-hidden className="text-default-300">
                ·
              </span>
              <span className="text-success-500">En ligne</span>
            </>
          )}
          {visibleLevels.map((level) => (
            <span
              key={level}
              className="rounded-full bg-default-100/70 px-1.5 py-0.5 text-[10.5px] font-medium text-default-600"
            >
              {STUDENT_LEVEL_LABELS[level] ?? level}
            </span>
          ))}
        </div>

        {/* Rating */}
        <div className="flex items-center gap-2 text-[12.5px]">
          <span className="inline-flex items-center gap-1 font-semibold text-foreground">
            <StarIcon />
            {listing.rating.toFixed(1)}
          </span>
          <span className="text-default-500">({listing.reviews} avis)</span>
        </div>

        {/* Description — 2 lines default, 3 lines featured */}
        <p
          className={clsx(
            "text-[13px] leading-relaxed text-default-600",
            isFeatured ? "line-clamp-3" : "line-clamp-2",
          )}
        >
          {listing.description}
        </p>

        <div className="flex-1" />

        {/* Footer: price + CTA */}
        <div className="flex items-end justify-between gap-2 border-t border-default-200/30 pt-3">
          <div>
            <p className="text-[10.5px] uppercase tracking-wide text-default-500">
              À partir de
            </p>
            <p
              className={clsx(
                "font-bold text-foreground",
                isFeatured ? "text-2xl" : "text-xl",
              )}
            >
              {listing.price}
              <span className="text-[11px] font-medium text-default-500">
                {" "}
                €/h
              </span>
            </p>
          </div>
          <Link
            className={clsx(
              "inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#FAB519] to-[#e09010] font-semibold text-[#191B36] shadow-md shadow-primary/25 transition-all hover:shadow-lg hover:shadow-primary/40 active:scale-95",
              isFeatured ? "px-5 py-2.5 text-[14px]" : "px-4 py-2 text-[13px]",
            )}
            href={detailHref}
          >
            {ctaLabel}
            <span aria-hidden>→</span>
          </Link>
        </div>
      </div>
    </m.article>
  );
}

function StarIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5 text-primary"
      fill="currentColor"
      viewBox="0 0 24 24"
    >
      <path d="M12 2l2.94 6.95L22 9.97l-5.17 4.88L18.18 22 12 18.27 5.82 22l1.35-7.15L2 9.97l7.06-1.02L12 2z" />
    </svg>
  );
}

function TrustBadge({
  tone,
  children,
}: {
  tone: "verified" | "top" | "fast";
  children: React.ReactNode;
}) {
  const palette: Record<typeof tone, string> = {
    verified: "bg-sky-500/95 text-white",
    top: "bg-primary/95 text-primary-foreground",
    fast: "bg-success-500/95 text-white",
  };

  return (
    <span
      className={clsx(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] font-semibold shadow-md backdrop-blur-sm",
        palette[tone],
      )}
    >
      {children}
    </span>
  );
}
